import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, putData, ImageFileUpload } from "../../../utility/Utility"; // Added putData
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

// Enum constants for validation
const GENDER_ENUM = ["male", "female", "other"];
const COMPENSATION_TYPE_ENUM = ["commission", "salary", "both"];
const PAYMENT_FREQUENCY_ENUM = ["monthly", "bi-weekly", "weekly"];
const JOB_STATUS_ENUM = [
  "part-time",
  "full-time",
  "self-employed",
  "unemployed",
];
const DESIGNATION_ENUM = [
  "insurer",
  "telecaller",
  "field-officer",
  "underwriter",
  "claims-adjuster",
  "sales-manager",
  "customer-support",
  "product-manager",
  "finance-officer",
  "compliance-officer",
  "backoffice",
  "guard",
  "manager",
];

export default function AddEmployee() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in edit mode
  const isEditMode = location.state?.employee;
  const employeeData = location.state?.employee;
  console.log("Employee Data:", employeeData);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    contactNumber: "",
    designation: "",
    image: null,
    imageUrl: "", // Added to store uploaded image URL
    isActive: true,
    dob: "",
    gender: "",
    compensationType: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    baseSalary: "",
    paymentFrequency: "monthly",
    jobStatus: "",
  });

  console.log("Form Data:", formData);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [formValid, setFormValid] = useState(false);

  // Helper function to format date for input[type="date"]
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Initialize form with employee data if in edit mode
  useEffect(() => {
    if (isEditMode && employeeData) {
      setFormData({
        username: employeeData.username || "",
        email: employeeData.email || "",
        contactNumber: employeeData.contactNumber || "",
        designation: employeeData.designation || "",
        image: null,
        imageUrl: employeeData.image || "",
        isActive:
          employeeData.isActive !== undefined ? employeeData.isActive : true,
        dob: employeeData.dob || "",
        gender: employeeData.gender || "",
        compensationType: employeeData.compensationType || "",
        address: {
          street: employeeData.address?.street || "",
          city: employeeData.address?.city || "",
          state: employeeData.address?.state || "",
          pincode: employeeData.address?.pincode || "",
          country: employeeData.address?.country || "India",
        },
        baseSalary: employeeData.salaryDetails?.baseSalary || "",
        paymentFrequency:
          employeeData.salaryDetails?.paymentFrequency || "monthly",
        jobStatus: employeeData.jobStatus || "",
      });
    }
  }, [isEditMode, employeeData]);

  // Validate individual field
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "username":
        if (!value.trim()) error = "Full name is required";
        else if (value.trim().length < 3)
          error = "Full name must be at least 3 characters";
        break;

      case "email":
        if (!value) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Please enter a valid email address";
        break;

      case "contactNumber":
        if (!value) error = "Mobile number is required";
        else if (!/^[0-9]{10}$/.test(value))
          error = "Please enter a valid 10-digit phone number";
        break;

      case "designation":
        if (!value) error = "Designation is required";
        else if (!DESIGNATION_ENUM.includes(value))
          error = "Please select a valid designation";
        break;

      case "image":
        // Image is only required for new employees, optional for updates
        if (!isEditMode && !formData.imageUrl && !value)
          error = "Image is required";
        break;

      case "dob":
        if (!value) error = "Date of birth is required";
        else {
          const dobDate = new Date(value);
          const today = new Date();
          if (dobDate >= today) error = "Date of birth must be in the past";
        }
        break;

      case "gender":
        if (!value) error = "Gender is required";
        else if (!GENDER_ENUM.includes(value))
          error = "Please select a valid gender";
        break;

      case "compensationType":
        if (!value) error = "Compensation type is required";
        else if (!COMPENSATION_TYPE_ENUM.includes(value))
          error = "Please select a valid compensation type";
        break;

      case "jobStatus":
        if (!value) error = "Job status is required";
        else if (!JOB_STATUS_ENUM.includes(value))
          error = "Please select a valid job status";
        break;

      case "baseSalary":
        if (!value) error = "Base salary is required";
        else if (parseFloat(value) <= 0)
          error = "Base salary must be greater than 0";
        break;

      case "address.street":
        if (!value.trim()) error = "Street address is required";
        break;

      case "address.city":
        if (!value.trim()) error = "City is required";
        break;

      case "address.state":
        if (!value.trim()) error = "State is required";
        break;

      case "address.pincode":
        if (!value.trim()) error = "Pincode is required";
        else if (!/^[0-9]{6}$/.test(value.trim()))
          error = "Pincode must be 6 digits";
        break;

      case "address.country":
        if (!value.trim()) error = "Country is required";
        break;

      default:
        break;
    }

    return error;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields
    newErrors.username = validateField("username", formData.username);
    newErrors.email = validateField("email", formData.email);
    newErrors.contactNumber = validateField(
      "contactNumber",
      formData.contactNumber
    );
    newErrors.designation = validateField("designation", formData.designation);
    newErrors.image = validateField("image", formData.image);
    newErrors.dob = validateField("dob", formData.dob);
    newErrors.gender = validateField("gender", formData.gender);
    newErrors.compensationType = validateField(
      "compensationType",
      formData.compensationType
    );
    newErrors.jobStatus = validateField("jobStatus", formData.jobStatus);
    newErrors.baseSalary = validateField("baseSalary", formData.baseSalary);
    newErrors["address.street"] = validateField(
      "address.street",
      formData.address.street
    );
    newErrors["address.city"] = validateField(
      "address.city",
      formData.address.city
    );
    newErrors["address.state"] = validateField(
      "address.state",
      formData.address.state
    );
    newErrors["address.pincode"] = validateField(
      "address.pincode",
      formData.address.pincode
    );
    newErrors["address.country"] = validateField(
      "address.country",
      formData.address.country
    );

    setErrors(newErrors);

    // Check if form is valid (no errors)
    const isValid = Object.values(newErrors).every((error) => error === "");
    setFormValid(isValid);

    return isValid;
  };

  // Update form validation when formData changes
  useEffect(() => {
    validateForm();
  }, [formData]);

  // Function to upload image immediately when selected
  const uploadImage = async (file) => {
    if (!file) return null;

    setImageUploading(true);
    const imageFormData = new FormData();
    imageFormData.append("file", file);

    try {
      const response = await ImageFileUpload(
        "/api/admin/upload?is_upload=s3",
        imageFormData
      );
      const data = await response;

      if (response?.success) {
        toast.success("Image uploaded successfully!");
        return {
          success: true,
          url: data.url || data.imageUrl || data.filePath,
        };
      } else {
        toast.error(data.message || "Image upload failed");
        return { success: false, error: data.message || "Image upload failed" };
      }
    } catch (err) {
      toast.error("Network error during image upload");
      return { success: false, error: "Network error during upload" };
    } finally {
      setImageUploading(false);
    }
  };

  const handleInputChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      const file = files[0];

      if (file) {
        // Validate file type and size
        const validTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
        ];
        const maxSize = 2 * 1024 * 1024; // 2MB

        if (!validTypes.includes(file.type)) {
          setErrors((prev) => ({
            ...prev,
            image: "Please select a valid image file (JPEG, PNG, GIF)",
          }));
          return;
        }

        if (file.size > maxSize) {
          setErrors((prev) => ({
            ...prev,
            image: "Image size must be less than 2MB",
          }));
          return;
        }

        // Clear previous errors and set the file
        setErrors((prev) => ({ ...prev, image: "" }));
        setFormData((prev) => ({ ...prev, image: file }));

        // Upload image immediately
        const uploadResult = await uploadImage(file);

        if (uploadResult.success) {
          setFormData((prev) => ({
            ...prev,
            imageUrl: uploadResult.url,
            image: file,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            image: uploadResult.error || "Image upload failed",
          }));
          setFormData((prev) => ({
            ...prev,
            image: null,
            imageUrl: isEditMode ? employeeData.image : "",
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          image: null,
          imageUrl: isEditMode ? employeeData.image : "",
        }));
      }
    } else if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const toggleActive = () => {
    setFormData({ ...formData, isActive: !formData.isActive });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched to show errors
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      if (key === "address") {
        Object.keys(formData.address).forEach((subKey) => {
          allTouched[`address.${subKey}`] = true;
        });
      } else {
        allTouched[key] = true;
      }
    });
    setTouched(allTouched);

    // Check if image is uploaded but URL is not available
    if (formData.image && !formData.imageUrl) {
      toast.error(
        "Please wait for image to finish uploading or select a different image"
      );
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix all errors before submitting");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        contactNumber: formData.contactNumber,
        designation: formData.designation,
        image: formData.imageUrl || (isEditMode ? employeeData.image : ""),
        dob: formData.dob,
        gender: formData.gender,
        compensationType: formData.compensationType,
        address: {
          street: formData.address.street.trim(),
          city: formData.address.city.trim(),
          state: formData.address.state.trim(),
          pincode: formData.address.pincode.trim(),
          country: formData.address.country.trim(),
        },
        salaryDetails: {
          baseSalary: parseFloat(formData.baseSalary),
          paymentFrequency: formData.paymentFrequency,
        },
        jobStatus: formData.jobStatus,
        isActive: formData.isActive,
      };

      let response;

      if (isEditMode) {
        // Update existing employee
        payload.id = employeeData._id; // Add the employee ID for update
        response = await postData("/api/employees/update", payload);

        if (response.status) {
          toast.success("Employee updated successfully!");
          navigate(-1);
        } else {
          toast.error(response.message || "Failed to update employee");
        }
      } else {
        // Create new employee
        response = await postData("/api/employees/create", payload);

        if (response.status) {
          toast.success(
            "Employee created successfully! Login details sent via WhatsApp."
          );
          navigate(-1);
        } else {
          toast.error(response.message || "Failed to add employee");
        }
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error(`${isEditMode ? "Update" : "Add"} employee error:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a field has error and is touched
  const hasError = (fieldName) => {
    return touched[fieldName] && errors[fieldName];
  };

  // Check if form is submitting (loading or image uploading)
  const isSubmitting = loading || imageUploading;

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Header & Breadcrumbs */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    {isEditMode ? "Update Employee" : "Create New Employee"}
                  </h5>
                </div>

                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Full Name */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="username"
                          className="form-label fw-medium"
                        >
                          Full Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="username"
                          name="username"
                          className={`form-control ${hasError("username") ? "is-invalid" : ""
                            }`}
                          placeholder="Enter full name"
                          value={formData.username}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        />
                        {hasError("username") && (
                          <div className="invalid-feedback d-block position-absolute">
                            {errors.username}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label htmlFor="email" className="form-label fw-medium">
                          Email <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`form-control ${hasError("email") ? "is-invalid" : ""
                            }`}
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        />
                        {hasError("email") && (
                          <div className="invalid-feedback d-block position-absolute">
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label
                          htmlFor="contactNumber"
                          className="form-label fw-medium"
                        >
                          Mobile <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="tel"
                          id="contactNumber"
                          name="contactNumber"
                          className={`form-control ${hasError("contactNumber") ? "is-invalid" : ""
                            }`}
                          placeholder="Enter contact number"
                          value={formData.contactNumber}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        />
                        {hasError("contactNumber") && (
                          <div className="invalid-feedback d-block position-absolute">
                            {errors.contactNumber}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Designation */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label
                          htmlFor="designation"
                          className="form-label fw-medium"
                        >
                          Designation <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="designation"
                          name="designation"
                          className={`form-select ${hasError("designation") ? "is-invalid" : ""
                            }`}
                          value={formData.designation}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Designation</option>
                          {DESIGNATION_ENUM.map((designation) => (
                            <option key={designation} value={designation}>
                              {designation
                                .split("-")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                            </option>
                          ))}
                        </select>
                        {hasError("designation") && (
                          <div className="invalid-feedback d-block position-absolute">
                            {errors.designation}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Image{" "}
                          {!isEditMode && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                      </div>
                      <div className="col-md-6">
                        <div className="position-relative">
                          <input
                            type="file"
                            id="image"
                            name="image"
                            className={`form-control ${hasError("image") ? "is-invalid" : ""
                              }`}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            accept="image/*"
                            disabled={isSubmitting}
                          />
                          {imageUploading && (
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div
                                className="spinner-border spinner-border-sm text-primary"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Uploading...
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <small className="text-muted d-block mt-1">
                          Recommended: 800x600px, Max 2MB (JPEG/PNG)
                          {imageUploading && " - Uploading image..."}
                          {isEditMode && " - Leave empty to keep current image"}
                        </small>
                        {hasError("image") && (
                          <div className="invalid-feedback d-block position-absolute">
                            {errors.image}
                          </div>
                        )}

                        {/* Show preview and upload status */}
                        {(formData.image || formData.imageUrl) &&
                          !imageUploading && (
                            <div className="mt-2">
                              <img
                                src={
                                  formData.imageUrl ||
                                  URL.createObjectURL(formData.image)
                                }
                                alt="Preview"
                                className="img-thumbnail"
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "150px",
                                }}
                              />
                              {formData.imageUrl && (
                                <div className="text-success mt-1">
                                  <i className="fas fa-check-circle me-1"></i>
                                  {formData.image
                                    ? "Image uploaded successfully!"
                                    : "Current image"}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Date of Birth */}
                    <div className="row mb-3 align-items-center mt-4">
                      <div className="col-md-3">
                        <label htmlFor="dob" className="form-label fw-medium">
                          Date of Birth <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="date"
                          id="dob"
                          name="dob"
                          className={`form-control ${hasError("dob") ? "is-invalid" : ""
                            }`}
                          value={formatDateForInput(formData?.dob)}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        />
                        {hasError("dob") && (
                          <div className="invalid-feedback d-block">
                            {errors.dob}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gender */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="gender"
                          className="form-label fw-medium"
                        >
                          Gender <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="gender"
                          name="gender"
                          className={`form-select ${hasError("gender") ? "is-invalid" : ""
                            }`}
                          value={formData.gender}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Gender</option>
                          {GENDER_ENUM.map((gender) => (
                            <option key={gender} value={gender}>
                              {gender.charAt(0).toUpperCase() + gender.slice(1)}
                            </option>
                          ))}
                        </select>
                        {hasError("gender") && (
                          <div className="invalid-feedback d-block">
                            {errors.gender}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Compensation Type */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="compensationType"
                          className="form-label fw-medium"
                        >
                          Compensation Type{" "}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="compensationType"
                          name="compensationType"
                          className={`form-select ${hasError("compensationType") ? "is-invalid" : ""
                            }`}
                          value={formData.compensationType}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Compensation Type</option>
                          {COMPENSATION_TYPE_ENUM.map((type) => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                          ))}
                        </select>
                        {hasError("compensationType") && (
                          <div className="invalid-feedback d-block">
                            {errors.compensationType}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Address <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <div className="row g-2">
                          <div className="col-12">
                            <input
                              type="text"
                              name="address.street"
                              className={`form-control ${hasError("address.street") ? "is-invalid" : ""
                                }`}
                              placeholder="Street Address"
                              value={formData.address.street}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isSubmitting}
                            />
                            {hasError("address.street") && (
                              <div className="invalid-feedback d-block">
                                {errors["address.street"]}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              name="address.city"
                              className={`form-control ${hasError("address.city") ? "is-invalid" : ""
                                }`}
                              placeholder="City"
                              value={formData.address.city}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isSubmitting}
                            />
                            {hasError("address.city") && (
                              <div className="invalid-feedback d-block">
                                {errors["address.city"]}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              name="address.state"
                              className={`form-control ${hasError("address.state") ? "is-invalid" : ""
                                }`}
                              placeholder="State"
                              value={formData.address.state}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isSubmitting}
                            />
                            {hasError("address.state") && (
                              <div className="invalid-feedback d-block">
                                {errors["address.state"]}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              name="address.pincode"
                              className={`form-control ${hasError("address.pincode") ? "is-invalid" : ""
                                }`}
                              placeholder="Pincode"
                              value={formData.address.pincode}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isSubmitting}
                            />
                            {hasError("address.pincode") && (
                              <div className="invalid-feedback d-block">
                                {errors["address.pincode"]}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              name="address.country"
                              className={`form-control ${hasError("address.country") ? "is-invalid" : ""
                                }`}
                              placeholder="Country"
                              value={formData.address.country}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              disabled={isSubmitting}
                            />
                            {hasError("address.country") && (
                              <div className="invalid-feedback d-block">
                                {errors["address.country"]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Base Salary */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="baseSalary"
                          className="form-label fw-medium"
                        >
                          Base Salary <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="number"
                          id="baseSalary"
                          name="baseSalary"
                          className={`form-control ${hasError("baseSalary") ? "is-invalid" : ""
                            }`}
                          placeholder="Enter base salary"
                          value={formData.baseSalary}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          min="0"
                          step="0.01"
                          disabled={isSubmitting}
                        />
                        {hasError("baseSalary") && (
                          <div className="invalid-feedback d-block">
                            {errors.baseSalary}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Frequency */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="paymentFrequency"
                          className="form-label fw-medium"
                        >
                          Payment Frequency{" "}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="paymentFrequency"
                          name="paymentFrequency"
                          className={`form-select ${hasError("paymentFrequency") ? "is-invalid" : ""
                            }`}
                          value={formData.paymentFrequency}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        >
                          {PAYMENT_FREQUENCY_ENUM.map((frequency) => (
                            <option key={frequency} value={frequency}>
                              {frequency
                                .split("-")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                            </option>
                          ))}
                        </select>
                        {hasError("paymentFrequency") && (
                          <div className="invalid-feedback d-block">
                            {errors.paymentFrequency}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Job Status */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="jobStatus"
                          className="form-label fw-medium"
                        >
                          Job Status <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="jobStatus"
                          name="jobStatus"
                          className={`form-select ${hasError("jobStatus") ? "is-invalid" : ""
                            }`}
                          value={formData.jobStatus}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Job Status</option>
                          {JOB_STATUS_ENUM.map((status) => (
                            <option key={status} value={status}>
                              {status
                                .split("-")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                            </option>
                          ))}
                        </select>
                        {hasError("jobStatus") && (
                          <div className="invalid-feedback d-block">
                            {errors.jobStatus}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Switch */}
                    {/* <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={toggleActive}
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                            disabled={isSubmitting}
                          />
                          <label className="form-check-label ms-2" htmlFor="isActive">
                            {formData.isActive ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-muted">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div> */}

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2 w-100"
                          disabled={
                            !formValid ||
                            isSubmitting ||
                            (formData.image && !formData.imageUrl)
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              {imageUploading
                                ? "Uploading Image..."
                                : "Processing..."}
                            </>
                          ) : isEditMode ? (
                            "Update Employee"
                          ) : (
                            "Create Employee"
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Error Summary */}
                    {!formValid && Object.keys(touched).length > 0 && (
                      <div className="row mt-3">
                        <div className="col-md-6 offset-md-3">
                          <div className="alert alert-warning mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Please fix all errors in the form before submitting.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image upload status */}
                    {formData.image &&
                      !formData.imageUrl &&
                      !imageUploading && (
                        <div className="row mt-3">
                          <div className="col-md-6 offset-md-3">
                            <div className="alert alert-danger mb-0">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              Image upload failed. Please select a different
                              image.
                            </div>
                          </div>
                        </div>
                      )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
